import React, { useState, useRef, useEffect } from "react";
import { X, Search, Check, ChevronDown } from "lucide-react";

import teamBadgePlaceholder from "../../../../assets/images/team_badge_placeholder.svg";
import { MODAL_TYPES } from "../../constants";

/**
 * Add Modal Component
 * Handles adding teams, referees, and administrators
 */
const AddModal = ({
  isOpen,
  modalType,
  onClose,
  // Team invite
  modalSearchQuery,
  onModalSearch,
  searchResults,
  isSearching,
  selectedTeams,
  onTeamSelect,
  onTeamRemove,
  onInviteTeam,
  // Manual team
  manualTeamName,
  setManualTeamName,
  manualTeamEmail,
  setManualTeamEmail,
  onCreateManualTeam,
  // Referee
  refereeName,
  setRefereeName,
  refereeEmail,
  setRefereeEmail,
  onAddReferee,
  // Admin
  adminName,
  setAdminName,
  adminEmail,
  setAdminEmail,
  onAddAdministrator,
  // Loading
  isLoading,
  // Category selection
  categories,
  selectedCategoryId,
  onCategoryChange,
}) => {
  const [teamSource, setTeamSource] = useState("clubbie"); // "clubbie" | "custom"

  // Reset team source when modal opens
  useEffect(() => {
    if (isOpen) setTeamSource("clubbie");
  }, [isOpen]);

  if (!isOpen) return null;

  const isTeamModal = modalType === MODAL_TYPES.TEAM;
  const isCustomMode = isTeamModal && teamSource === "custom";

  const getTitle = () => {
    switch (modalType) {
      case MODAL_TYPES.TEAM:
        return "Invite Team";
      case MODAL_TYPES.REFEREE:
        return "Add Referee";
      case MODAL_TYPES.ADMIN:
        return "Add Administrator";
      default:
        return "Add";
    }
  };

  const getSubmitHandler = () => {
    if (isCustomMode) return onCreateManualTeam;
    switch (modalType) {
      case MODAL_TYPES.TEAM:
        return onInviteTeam;
      case MODAL_TYPES.REFEREE:
        return onAddReferee;
      case MODAL_TYPES.ADMIN:
        return onAddAdministrator;
      default:
        return () => {};
    }
  };

  const getSubmitLabel = () => {
    if (isCustomMode) return isLoading ? "Creating..." : "Add";
    if (isTeamModal) return isLoading ? "Inviting..." : "Invite team";
    return isLoading ? "Inviting..." : "Invite";
  };

  const isSubmitDisabled = () => {
    if (isLoading) return true;
    if (isTeamModal && !isCustomMode) return !selectedTeams || selectedTeams.length === 0;
    return false;
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-app-bg w-full max-w-lg mx-3 rounded-2xl border border-app-border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 py-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 mb-4">
          <h3 className="text-[15px] font-medium text-[#FEFCEB]">{getTitle()}</h3>
          <button
            onClick={onClose}
            className="text-clubbie-white hover:text-[#FEFCEB] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 mb-4">
          {/* Category Selector - shown for team modal */}
          {isTeamModal && categories?.length > 1 && (
            <CategorySelector
              categories={categories}
              selectedCategoryId={selectedCategoryId}
              onCategoryChange={onCategoryChange}
            />
          )}

          {/* Team source toggle - Clubbie vs Custom */}
          {isTeamModal && (
            <div className="mb-4">
              <label className="block text-[14px] font-normal text-app-text mb-2">
                Source
              </label>
              <div className="flex gap-[12px]">
                {[
                  { value: "clubbie", label: "From Clubbie" },
                  { value: "custom", label: "Custom team" },
                ].map((opt) => (
                  <label key={opt.value} className="flex items-center gap-[8px] cursor-pointer">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${teamSource === opt.value ? "border-[#00E7FF]" : "border-[#333E48]"}`}>
                      {teamSource === opt.value && <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />}
                    </div>
                    <input type="radio" className="hidden" checked={teamSource === opt.value} onChange={() => setTeamSource(opt.value)} />
                    <span className="text-sm text-[#FEFCEB]">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Clubbie search form */}
          {isTeamModal && !isCustomMode && (
            <TeamSearchForm
              searchQuery={modalSearchQuery}
              onSearch={onModalSearch}
              searchResults={searchResults}
              isSearching={isSearching}
              selectedTeams={selectedTeams}
              onTeamSelect={onTeamSelect}
              onTeamRemove={onTeamRemove}
            />
          )}

          {/* Custom team form */}
          {isCustomMode && (
            <ManualTeamForm
              teamName={manualTeamName}
              setTeamName={setManualTeamName}
              teamEmail={manualTeamEmail}
              setTeamEmail={setManualTeamEmail}
            />
          )}

          {modalType === MODAL_TYPES.REFEREE && (
            <PersonForm
              name={refereeName}
              setName={setRefereeName}
              email={refereeEmail}
              setEmail={setRefereeEmail}
              namePlaceholder="Enter referee name here"
              emailPlaceholder="referee@example.com"
            />
          )}

          {modalType === MODAL_TYPES.ADMIN && (
            <PersonForm
              name={adminName}
              setName={setAdminName}
              email={adminEmail}
              setEmail={setAdminEmail}
              namePlaceholder="Enter administrator name here"
              emailPlaceholder="admin@example.com"
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={getSubmitHandler()}
            disabled={isSubmitDisabled()}
            className="bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {getSubmitLabel()}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Manual Team Form
 */
const ManualTeamForm = ({ teamName, setTeamName, teamEmail, setTeamEmail }) => (
  <div className="space-y-4">
    <FormField
      label="Team name"
      value={teamName}
      onChange={(e) => setTeamName(e.target.value)}
      placeholder="Enter team name here"
      autoFocus
    />
    <FormField
      label="Email"
      type="email"
      value={teamEmail}
      onChange={(e) => setTeamEmail(e.target.value)}
      placeholder="team@example.com"
    />
  </div>
);

/**
 * Person Form (Referee/Admin)
 */
const PersonForm = ({
  name,
  setName,
  email,
  setEmail,
  namePlaceholder,
  emailPlaceholder,
}) => (
  <div className="space-y-4">
    <FormField
      label="Name"
      value={name}
      onChange={(e) => setName(e.target.value)}
      placeholder={namePlaceholder}
      autoFocus
    />
    <FormField
      label="Email"
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder={emailPlaceholder}
    />
  </div>
);

/**
 * Team Search Form
 */
const TeamSearchForm = ({
  searchQuery,
  onSearch,
  searchResults,
  isSearching,
  selectedTeams = [],
  onTeamSelect,
  onTeamRemove,
}) => (
  <div className="space-y-4">
    <div>
      <label className="block text-[14px] font-normal text-app-text mb-1.5 ">
        Team name
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
          <Search size={16} />
        </div>
        <input
          type="text"
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
          className="w-full bg-blue-shade-8 border border-blue-shade-8 rounded-3xl pl-9 pr-4 py-2.5 text-[14px] text-[#FEFCEB] placeholder-app-text-secondary focus:border-[#00E7FF] outline-none transition-colors"
          autoFocus
        />
      </div>
    </div>

    {/* Selected teams chips */}
    {selectedTeams.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {selectedTeams.map((team) => {
          const tId = team._id || team.id;
          return (
            <div
              key={tId}
              className="flex items-center gap-2 bg-app-surface border border-app-border rounded-full pl-1.5 pr-2 py-1"
            >
              <img
                src={team?.crest || team.logo || teamBadgePlaceholder}
                alt={team.name || team.title}
                className={`w-5 h-5 rounded-full object-cover`}
              />
              <span className="text-xs font-medium text-[#FEFCEB]">
                {team.name || team.title}
              </span>
              <button
                type="button"
                onClick={() => onTeamRemove(tId)}
                className="text-app-text-muted hover:text-[#FEFCEB] transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>
    )}

    <div className="h-[250px] overflow-y-auto custom-scrollbar flex flex-col border border-app-border rounded-2xl">
      {isSearching ? (
        <div className="text-center py-8 text-app-text-secondary text-sm">Searching...</div>
      ) : searchQuery && searchResults.length === 0 ? (
        <div className="text-center py-8 text-app-text-secondary text-sm">No teams found</div>
      ) : searchResults.length > 0 ? (
        searchResults.map((team) => {
          const tId = team._id || team.id;
          const isSelected = selectedTeams.some(
            (t) => (t._id || t.id) === tId
          );
          return (
            <div
              key={tId}
              onClick={() => onTeamSelect(team)}
              className={`flex items-center gap-3 px-3 py-3 cursor-pointer border-b border-app-border transition-all ${
                isSelected
                  ? "bg-[#00E7FF]/10 text-[#FEFCEB]"
                  : "bg-transparent hover:bg-[#082441] text-gray-300"
              }`}
            >
              <img
                src={team?.crest || team.logo || teamBadgePlaceholder}
                alt={team.name || team.title}
                className="w-5 h-5 rounded-full object-cover bg-gray-800"
              />
              <span className="text-sm font-medium">{team.name || team.title}</span>
              {isSelected && <Check size={16} className="ml-auto text-[#00E7FF]" />}
            </div>
          );
        })
      ) : (
        <div className="text-center py-8 text-app-text-secondary text-sm">
          Start typing to search for teams
        </div>
      )}
    </div>
  </div>
);

/**
 * Form Field Component
 */
const FormField = ({
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  autoFocus = false,
}) => (
  <div>
    <label className="block text-[14px] font-normal text-clubbie-white mb-1.5 ">
      {label}
    </label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoFocus={autoFocus}
      className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
    />
  </div>
);

/**
 * Category Selector (custom popover dropdown)
 */
const CategorySelector = ({ categories, selectedCategoryId, onCategoryChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  const selectedLabel = categories.find((c) => c._id === selectedCategoryId)?.label || categories[0]?.label;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="mb-4">
      <label className="block text-[14px] font-normal text-app-text mb-1.5 ">
        Category
      </label>
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] focus:border-blue-shade-2 outline-none transition-colors flex items-center justify-between"
        >
          <span>{selectedLabel}</span>
          <ChevronDown
            size={20}
            className={`text-clubbie-white transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute left-0 top-full mt-2 w-full bg-[#000F1F] border border-[#192634] rounded-2xl shadow-xl z-50 overflow-hidden">
            {categories.map((cat, i) => (
              <button
                key={cat._id}
                type="button"
                onClick={() => {
                  onCategoryChange(cat._id);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-4 py-3 text-sm font-medium flex items-center gap-1.5 transition-colors ${
                  i < categories.length - 1 ? "border-b border-[#192634]" : ""
                } ${
                  cat._id === selectedCategoryId
                    ? "text-app-accent"
                    : "text-[#FEFCEB] hover:bg-[#0D1B29]"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AddModal;
